'use client';

// Report an error caught by a React error boundary to the same pipeline the
// global listener uses.
//
// WHY THIS EXISTS AT ALL: React does NOT re-throw to `window.onerror` an error
// that an error boundary catches. Every `error.tsx` in this app is therefore a
// hole punched in the `window.addEventListener('error')` listener in
// `capture.ts` — an error reported today would go silent the moment a boundary
// is added above it. Each boundary must report for itself, or adding boundaries
// quietly reduces our error telemetry, which is worse than having no boundary.
//
// So: every `error.tsx` in this repo calls this hook. If you add one, call it.

import { useEffect, useRef } from 'react';
import { captureError, crumb } from './capture';

/** Next hands a boundary this shape: a plain Error, plus `digest` when the
 *  throw happened on the server and the message was scrubbed for the client. */
export type BoundaryError = Error & { digest?: string };

/**
 * @param error    the error Next passed to the boundary
 * @param boundary which boundary caught it, so triage can tell a Review crash
 *                 from a whole-app crash without reading the stack
 */
export function useErrorReport(error: BoundaryError, boundary: string): void {
  // React 18 StrictMode double-invokes effects in development, and `reset()`
  // can re-render with the same error object. Both would post twice; the
  // fingerprint debounce in captureError would collapse them, but only inside
  // its 10s window, so key on the error itself rather than relying on that.
  const reported = useRef<BoundaryError | null>(null);

  useEffect(() => {
    if (reported.current === error) return;
    reported.current = error;

    crumb('boundary.caught', { boundary, digest: error.digest ?? null });
    captureError(error, {
      extra: {
        source: 'error-boundary',
        boundary,
        // Present only for a server-side throw. It is the join key to the
        // matching server log line, since the message itself is scrubbed.
        digest: error.digest ?? null,
        // Next's error.tsx contract does not include React's component stack —
        // only the error. `error.stack` is what postEvent already sends. Named
        // here so the next person does not go looking for a field we cannot get
        // from this boundary shape.
        component_stack: null,
      },
    });
  }, [error, boundary]);
}
